import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaDumbbell, FaChartLine, FaPlay, FaPlus, FaSearch, FaFilter, FaTimes, FaEdit, FaFileImport } from 'react-icons/fa';
import SEO from '../SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../../utils/seoConfig';
import './FitnessHome.css';

const FitnessHome = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedFocus, setSelectedFocus] = useState('');
  const [selectedVisibility, setSelectedVisibility] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/programs`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      if (!response.ok) throw new Error('Failed to fetch programs');
      const data = await response.json();
      setPrograms(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const filterOptions = useMemo(() => ({
    difficulties: [...new Set(programs.map(program => program.difficulty).filter(Boolean))].sort(),
    focusAreas: [...new Set(programs.flatMap(program => program.focus || []).filter(Boolean))].sort()
  }), [programs]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const filteredPrograms = useMemo(() => programs.filter((program) => {
    const matchesSearch = !normalizedSearchTerm
      || program.name?.toLowerCase().includes(normalizedSearchTerm)
      || program.description?.toLowerCase().includes(normalizedSearchTerm)
      || program.goal?.toLowerCase().includes(normalizedSearchTerm)
      || program.focus?.some(item => item.toLowerCase().includes(normalizedSearchTerm));

    const matchesDifficulty = !selectedDifficulty || program.difficulty === selectedDifficulty;
    const matchesFocus = !selectedFocus || program.focus?.includes(selectedFocus);
    const matchesVisibility =
      !selectedVisibility
      || (selectedVisibility === 'coach'
        ? Boolean(program.fromCoach)
        : selectedVisibility === 'mine'
          ? Boolean(program.isOwn)
          : selectedVisibility === 'library'
            ? !program.fromCoach
            : false);

    return matchesSearch && matchesDifficulty && matchesFocus && matchesVisibility;
  }), [normalizedSearchTerm, selectedDifficulty, selectedFocus, selectedVisibility, programs]);

  const hasActiveFilters = Boolean(
    normalizedSearchTerm || selectedDifficulty || selectedFocus || selectedVisibility
  );

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDifficulty('');
    setSelectedFocus('');
    setSelectedVisibility('');
  };

  if (loading) {
    return (
      <div className="fitness-home">
        <div className="fitness-loading">
          <div className="loading-spinner" />
          <p>Loading programs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fitness-home">
        <div className="fitness-error">
          <p>Error loading programs: {error}</p>
          <button onClick={fetchPrograms}>Try Again</button>
        </div>
      </div>
    );
  }

  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const seo = PAGE_SEO['/fitness'];

  return (
    <div className="fitness-home">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/fitness"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/fitness',
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Fitness Programs', url: '/fitness' },
          ]),
        ]}
      />
      <div className="fitness-hero">
        <div className="hero-content">
          <h1>Your Fitness Journey</h1>
          <p>Structured programs designed for your goals</p>
          {user && (
            <div className="fitness-hero-actions">
              <button type="button" className="create-btn import-pdf-btn" onClick={() => navigate('/fitness/program/import')}>
                <FaFileImport /> Import programs
              </button>
              <button type="button" className="create-btn" onClick={() => navigate('/fitness/program/create')}>
                <FaPlus /> Create Program
              </button>
            </div>
          )}
        </div>
        <div className="hero-pattern" />
      </div>

      <div className="fitness-container">
        <section className="programs-section">
          <div className="section-header">
            <h2>Available Programs</h2>
            <p>Choose a program to get started</p>
          </div>

          <div className="programs-search-filter-section">
            <div className="programs-search-box">
              <FaSearch className="programs-search-icon" />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="programs-clear-search" onClick={() => setSearchTerm('')}>
                  <FaTimes />
                </button>
              )}
            </div>

            <button
              className={`programs-filter-toggle ${showFilters ? 'programs-filter-active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter />
              <span>Filters</span>
              {hasActiveFilters && <span className="programs-filter-badge" />}
            </button>
          </div>

          {showFilters && (
            <div className="programs-filters-panel">
              <div className="programs-filter-group">
                <label>Difficulty</label>
                <select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)}>
                  <option value="">All Levels</option>
                  {filterOptions.difficulties.map(difficulty => (
                    <option key={difficulty} value={difficulty}>{difficulty}</option>
                  ))}
                </select>
              </div>

              <div className="programs-filter-group">
                <label>Focus</label>
                <select value={selectedFocus} onChange={(e) => setSelectedFocus(e.target.value)}>
                  <option value="">All Focus Areas</option>
                  {filterOptions.focusAreas.map(focus => (
                    <option key={focus} value={focus}>{focus}</option>
                  ))}
                </select>
              </div>

              <div className="programs-filter-group">
                <label>Source</label>
                <select value={selectedVisibility} onChange={(e) => setSelectedVisibility(e.target.value)}>
                  <option value="">All programs</option>
                  <option value="mine">Created by me</option>
                  <option value="library">Library programs</option>
                  <option value="coach">Coach programs</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button className="programs-clear-filters" onClick={clearFilters}>
                  Clear All Filters
                </button>
              )}
            </div>
          )}

          <div className="programs-results-count">
            Showing {filteredPrograms.length} of {programs.length} program{programs.length !== 1 ? 's' : ''}
          </div>

          <div className="programs-grid">
            {filteredPrograms.map((program) => (
              <div 
                key={program.programId} 
                className="program-card"
                onClick={() => navigate(`/fitness/program/${program.programId}`)}
              >
                {program.image && (
                  <div className="program-card-image-wrap">
                    <img
                      src={program.image}
                      alt=""
                      className="program-card-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="program-card-header">
                  <div 
                    className="difficulty-badge"
                    style={{ backgroundColor: getDifficultyColor(program.difficulty) }}
                  >
                    {program.difficulty}
                  </div>
                  <div className="program-icon">
                    <FaDumbbell />
                  </div>
                </div>

                <div className="program-card-body">
                  <h3 title={program.name}>{program.name}</h3>
                  {user && program.isOwn && (
                    <span className="own-badge">My program</span>
                  )}
                  {program.fromCoach && (
                    <span className="coach-badge"><i className="fas fa-user-tie"></i> {program.coachName || 'Your Coach'}</span>
                  )}
                  <p className="program-description" title={program.description || undefined}>{program.description}</p>

                  <div className="program-meta">
                    <div className="meta-item">
                      <FaClock className="meta-icon" />
                      <span>{program.sessionDuration}</span>
                    </div>
                    <div className="meta-item">
                      <FaChartLine className="meta-icon" />
                      <span>{program.daysPerWeek} days/week</span>
                    </div>
                    <div className="meta-item">
                      <span>{program.durationWeeks} weeks</span>
                    </div>
                  </div>

                  <div className="program-focus">
                    {program.focus?.slice(0, 4).map((item, idx) => (
                      <span key={idx} className="focus-tag">{item}</span>
                    ))}
                  </div>
                </div>

                <div className="program-card-footer">
                  {user && program.isOwn && program._id && (
                    <button
                      type="button"
                      className="edit-program-card-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/fitness/program/${program._id}/edit`);
                      }}
                    >
                      <FaEdit />
                      <span>Edit</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="start-program-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/fitness/program/${program.programId}`);
                    }}
                  >
                    <FaPlay />
                    <span>View Program</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredPrograms.length === 0 && (
            <div className="no-programs">
              <p>{programs.length === 0 ? 'No programs available yet. Check back soon!' : 'No programs match your search or filters.'}</p>
              {hasActiveFilters && (
                <button className="programs-empty-reset" onClick={clearFilters}>Clear Filters</button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default FitnessHome;

